import React from 'react';
import { C, soft } from '../../constants/theme.js';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { fmtCoins } from '../../constants/creditPricing.js';
import { type } from '../../constants/typography.js';

// The «پارسه» vocabulary, so every credit surface spells it the same way: one
// glyph and one cost pill. Identical to the doctor client's CoinChip — a
// dentist who also uses that panel is looking at one balance, and it should
// look like one balance.

/** The coin glyph — a ring with an inner dot, drawn in currentColor. */
export function CoinGlyph({ size = 12 }) {
  return (
    <svg viewBox="0 0 16 16" width={size} height={size} fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="8" cy="8" r="2.1" fill="currentColor" />
    </svg>
  );
}

/**
 * A cost pill: «۱۰» / «۵ / دقیقه» / «این جلسه: ۲۳».
 *
 * Renders NOTHING when `cost` is null or undefined — an unpriced or disabled
 * action must not advertise a price.
 *
 * tone: 'accent' (default, teal) | 'onDark' (over a gradient) | 'warn'
 *     | 'danger' | 'mute'
 */
export default function CoinChip({
  cost, per, prefix, tone = 'accent', size = 'sm', title, style, testid = 'coin-chip',
}) {
  const { formatNumber } = useTranslation();
  if (cost == null) return null;

  const tones = {
    accent: { bg: soft(C.teal, 0.14), fg: C.teal },
    onDark: { bg: 'rgba(255,255,255,0.18)', fg: '#fff' },
    warn: { bg: C.amberLight, fg: C.amber },
    danger: { bg: C.redLight, fg: C.red },
    mute: { bg: soft(C.textMuted, 0.12), fg: C.textSecondary },
  };
  const palette = tones[tone] || tones.accent;
  const isSm = size === 'sm';

  return (
    <span
      title={title}
      data-testid={testid}
      style={{
        ...(isSm ? type.caption : type.label),
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: palette.bg,
        color: palette.fg,
        borderRadius: 999,
        padding: isSm ? '1px 8px' : '3px 10px',
        fontWeight: 800,
        lineHeight: 1.6,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        ...style,
      }}
    >
      <CoinGlyph size={isSm ? 11 : 12} />
      {prefix ? `${prefix} ` : ''}
      {fmtCoins(cost, formatNumber)}
      {/* The unit word is never rendered inside the pill — the glyph IS the
          unit, which is what keeps a pill the same width in fa and en. */}
      {per ? ` / ${per}` : ''}
    </span>
  );
}
